import { useState } from "react";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import fileDownload from "js-file-download";
import reportingApi from "../../api/reportingApi";
import LocalStorage from "../../sharedComponents/LocalStorage/LocalStorage";
import useManufacturerData from "../../sharedComponents/useManufacturerData/useManufacturerData";

const useReports = () => {
    const mfrId = LocalStorage.manufacturerData.get().platform_id;
    const userId = LocalStorage.userData.get().user.id || '';
    const portalBrand = localStorage.getItem('branding') || '';
    const portalType = portalBrand === 'ARROWSTREAM' ? 'SalesStream' : 'BEP';
    const [selectedReportPage, setSelectedReportPage] = useState(1);
    const { manufacturerParams } = useManufacturerData();
    const useReportsList = () => useQuery({
        queryKey: ['reportsList'],
        queryFn: () => reportingApi.getAllReports.get().then(r => r.data)
    });

    const getReportFilters = () => {
        const params = {
            portal: portalType,
            ...manufacturerParams
        }
        return reportingApi.getReportFilters.post(params);
    };
    const useReportsFilters = () => useQuery({
        queryKey: ['reportsFilters'],
        queryFn: () => getReportFilters().then(r => r.data)
    });

    const fetchReportContent = (id, page, manufacturers) => {
        if (id && mfrId) {
            const params = {
                id: id,
                manufacturer: mfrId,
                page: page,
                manufacturers: (manufacturers ?? []).map((m) => m?.value),
            }
            return reportingApi.getReportContentPage.post(params);
        }
    };
    const useReportContent = (id, page, manufacturers) => useQuery({
        queryKey: ['reportContent', id, page, manufacturers],
        queryFn: () => fetchReportContent(id, page, manufacturers).then(r => r.data)
    });

    const [isExporting, setIsExporting] = useState(false);
    const portalTypeReportsList = useReportsList().data?.filter((report) => report.reportType.portal === portalType) ?? [];
    const recentReportsList = portalTypeReportsList?.sort((a, b) => new Date(b.lastEdited) - new Date(a.lastEdited)).slice(0, 10)?? [];
    const favoritedReportsList = portalTypeReportsList?.filter(report => {return report.favourite === true}) ?? [];
    const userReportsList = portalTypeReportsList?.filter(report => {return report.ownerId === userId}) ?? [];

    const getSelectedReportMetadata = (reportId, reportsList) => {
        const reportMetadata = reportsList?.find((report) => report.id === +reportId);
        return reportMetadata;
    };

    const exportReport = async (reportId, reportName, mfrId) => {
        if (reportId && mfrId) {
            const params = {
                id: reportId,
                manufacturer: mfrId,
                ...manufacturerParams
            }
            setIsExporting(true);
            const loadingToast = toast.loading("Fetching data. This may take a moment...");
            reportingApi.exportReport.post(params, { responseType: 'blob' }).then((response) => {
                toast.update(loadingToast, { render: "Downloading CSV file..", type: "success", isLoading: false, autoClose: "10000" });
                const date = new Date().toISOString().split('T')[0];
                fileDownload(response.data, `${reportName}_${date}.csv`);
                setIsExporting(false);
            }).catch((error) => {
                toast.update(loadingToast, { render: "Error in exporting report", type: "warn", isLoading: false, autoClose: "10000" });
                console.error('Error fetching report data:', error);
                setIsExporting(false);
            });
        }
    }

    return {
        exportReport,
        isExporting,
        portalType,
        portalBrand,
        useReportsList,
        useReportsFilters,
        useReportContent,
        portalTypeReportsList,
        recentReportsList,
        userReportsList,
        favoritedReportsList,
        selectedReportPage,
        setSelectedReportPage,
        getSelectedReportMetadata
    };
}

export default useReports;
